import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { localizedStrings } from '@core/strings'
import { useModal } from '@common/GlobalModal'
import { ModalDialogContainer } from '@common/modal'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Permit } from '@core/entities/permit/Permit'
import { PermitBase } from '@core/entities/permit/PermitBase'
import { PageUrls } from '@core/page-urls'

// The modal only renders `id` + `name`. Accept the slim structural shape so
// both `PermitTemplateSummary` (from the parent's `allowedChildTemplates`)
// and `PermitTemplate` (from the create-list endpoint) flow through without
// gymnastics.
type AllowedChild = { id: number; name: string }

type Props = {
  parentPermit: Permit | PermitBase
  projectId: number
  allowedChildren: Array<AllowedChild>
  onDismiss?: () => void
}

export const SelectChildPermitModal: React.FC<Props> = ({
  parentPermit,
  projectId,
  allowedChildren,
  onDismiss,
}) => {
  const router = useRouter()
  const { hideModal } = useModal()
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('')

  const handleDismiss = () => {
    hideModal()
    onDismiss?.()
  }

  const handleConfirm = () => {
    if (!selectedTemplateId) return
    router.push(
      PageUrls.newPermitProject({
        organisationId: parentPermit.organisationId,
        projectId,
        query: {
          parentPermitId: parentPermit.id,
          templateid: selectedTemplateId,
        },
      }),
    )
    hideModal()
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.wouldYouLikeToRaiseAnyAdditionalChildPermit}
        <IconButton aria-label='close-modal' onClick={handleDismiss}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TextField
          select
          fullWidth
          label={localizedStrings.childPermits}
          value={selectedTemplateId}
          onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
          data-test='select-child-permit-template'
          sx={{ mt: 1 }}
        >
          {allowedChildren.map((child) => (
            <MenuItem key={child.id} value={child.id}>
              {child.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={handleDismiss}>
          {localizedStrings.noImDone}
        </Button>
        <Button
          variant='contained'
          disabled={!selectedTemplateId}
          onClick={handleConfirm}
          data-test='select-child-permit-confirm'
        >
          {localizedStrings.confirm}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
